import styled from '@emotion/styled';
import type { AvatarResponse, OutfitResponse } from '@innovationdepartment/proxima-sdk-axios';
import { colors, Pill, Text } from '@innovationdepartment/proxima-ui';

import type { GetDisplay } from '../useMetadataDisplay';

/** The SDK does not type outfit image URLs yet; the platform is adding a presigned `url`. */
type OutfitWithUrl = OutfitResponse & { url?: string };

const CardWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.neutral20};
  border-radius: 8px;
  background-color: ${colors.white};
  overflow: hidden;
`;

const CardThumbnail = styled.div`
  position: relative;
  aspect-ratio: 4 / 5;
  background-color: ${colors.neutral10};
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  flex: 1;
`;

/* body1Semibold is 16px/24px — reserve exactly two lines (and clamp to two)
   so the age/gender and attribute rows line up across cards. */
const CardName = styled.div`
  height: 48px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

type AvatarCardProps = {
  avatar: AvatarResponse;
  getDisplay: GetDisplay;
};

const AvatarCard = ({ avatar, getDisplay }: AvatarCardProps) => {
  const imageUrl = (avatar.outfitImages?.[0] as OutfitWithUrl | undefined)?.url;
  const attributeLines = [
    avatar.ethnicity?.length ? avatar.ethnicity.map(getDisplay).join(' · ') : null,
    avatar.build && getDisplay(avatar.build)
  ].filter((line): line is string => Boolean(line));

  return (
    <CardWrapper>
      <CardThumbnail>
        {imageUrl ? <CardImage src={imageUrl} alt={avatar.displayName} loading="lazy" /> : null}
      </CardThumbnail>
      <CardBody>
        <CardName>
          <Text variant="body1Semibold">{avatar.displayName}</Text>
        </CardName>
        <Text variant="tag2Semibold" color="neutral80">
          {`${getDisplay(avatar.gender)} · ${avatar.age}`}
        </Text>
        {attributeLines.map(line => (
          <Text key={line} variant="tag2Medium" color="neutral70">
            {line}
          </Text>
        ))}
        {avatar.brandExclusive ? <Pill label="Brand exclusive" variant="info" /> : null}
      </CardBody>
    </CardWrapper>
  );
};

export default AvatarCard;
